/**
 * product_metadata 域 Zod 共享契约 —— admin 运营录入「业务效果」(per-通道 (vendor_product_id, payment_method) → coin/bonus)
 *
 * 契约源：dev-docs `03-数据字典/70-产品配置.md` §2（字段字典，per-通道 PK）+ `02-API契约/A8-运营支付.md` §1-5。
 *
 * 设计：
 * - product_metadata = C 端 baseline own 镜像表（schema/shared/product-metadata.ts）；admin 拥运营写
 * - per-通道：PK = (app_id, vendor_product_id, payment_method)——同 product 在 IAP / STRIPE 各一行，各自
 *   coin/bonus 可不同（bonus_ratio 改响应派生）；product 级字段（product_type/subscription_effect/subscription_period/
 *   name/title_i18n）跨通道行应同值；title_i18n 的手工 PATCH 与自动补齐由服务端按产品级原子写入
 * - 「业务效果」(coin_amount / bonus_amount / bonus_expire_days) 是 Adapty 不覆盖、无源可同步、必须运营人填的
 *   字段（Adapty 只管收款 + access level，不管加积分）。`COIN_PERIOD_GRANT` 订阅的 coin / bonus 是每计费周期
 *   一次性发放量，规则与 CONSUMABLE 同一套（IAP 人工维护 + STRIPE 派生下限，70 §2）
 * - `daily_grant_amount` 是**冻结列**（70 §2）：不进入请求与响应，请求携带即按未声明键 VALIDATION_FAILED；
 *   新行取 DB 默认 0，判定与派生均不读它
 * - vendor_product_id / payment_method 是 Adapty 身份 + PK 组件：admin 只「按 Adapty 上的 ID 按通道录入新行」，
 *   **创建后不可改**（改 ID / 通道 = 软删旧行 + 加新行）→ Update 契约 .strict() 拒一切身份/不可变字段
 * - 金额 bigint minor units → API 序列化为 string（非负整数串，上界 int8 防写 DB 溢出）
 * - bonus_ratio 是**响应派生字段**（deriveProductBonusRatio，非 DB 列、不录入）；STRIPE 权益由服务端按
 *   apps.third_party_bonus_ratio 派生下限受控上调（#465）——录入契约不含 bonus_ratio，误传即 VALIDATION_FAILED
 */

import { z } from 'zod';
import type { ChannelPriceSummaryItem } from './payment-channel-prices';
import {
  type SettlementChannel,
  SettlementChannelSchema,
  isFrameworkSettlementChannel,
} from './payment-methods';
import type { ProductValidationIssue } from './product-metadata-serveability';
import { LANGUAGE_CODE_RE } from './system-config';

/** product_type 枚举闭集（镜像 schema ProductTypeLiteral 直接派生本类型 —— 单一真值源） */
export const PRODUCT_TYPES = ['CONSUMABLE', 'SUBSCRIPTION'] as const;
export const ProductTypeSchema = z.enum(PRODUCT_TYPES);
export type ProductType = z.infer<typeof ProductTypeSchema>;

/** subscription_period 枚举闭集（仅 SUBSCRIPTION；镜像 schema SubscriptionPeriodLiteral 直接派生） */
export const SUBSCRIPTION_PERIODS = ['WEEKLY', 'MONTHLY', 'QUARTERLY', 'YEARLY'] as const;
export const SubscriptionPeriodSchema = z.enum(SUBSCRIPTION_PERIODS);
export type SubscriptionPeriod = z.infer<typeof SubscriptionPeriodSchema>;

/**
 * 对外订阅效果闭集（`03-数据字典/70-产品配置.md` §2 `subscription_effect`、A8 §2）：`COIN_PERIOD_GRANT` =
 * 每计费周期（首购 / 续订 / RESUBSCRIBE）一次性发放该周期 coin_amount + bonus_amount；`VIP_READING` 不发币。
 * 请求只收这两个值，响应只出这两个值。
 */
export const SUBSCRIPTION_EFFECTS = ['COIN_PERIOD_GRANT', 'VIP_READING'] as const;
export const SubscriptionEffectSchema = z.enum(SUBSCRIPTION_EFFECTS);
export type SubscriptionEffect = z.infer<typeof SubscriptionEffectSchema>;

/**
 * 库内存储值闭集 = 对外闭集 + 待 C 端回填的 `COIN_DAILY_BONUS`（与 `COIN_PERIOD_GRANT` 是同一效果的两个
 * 存储值，70 §2「存量迁移」）。镜像 schema 的列 enum 与 DB CHECK `product_metadata_subscription_effect_shape`
 * 同口径；回填由 C 端执行，admin 不改写已存值（DB 触发器只放行 `COIN_DAILY_BONUS → COIN_PERIOD_GRANT`）。
 */
export const STORED_SUBSCRIPTION_EFFECTS = [
  'COIN_DAILY_BONUS',
  'COIN_PERIOD_GRANT',
  'VIP_READING',
] as const;
export type StoredSubscriptionEffect = (typeof STORED_SUBSCRIPTION_EFFECTS)[number];

/**
 * 存储值 → 对外效果：`COIN_DAILY_BONUS` / `COIN_PERIOD_GRANT` 归一为 `COIN_PERIOD_GRANT`。
 * NULL 与未登记值返回 null，由调用方 fail-closed（不按名字或金额猜效果）。
 */
export function normalizeSubscriptionEffect(
  stored: string | null | undefined,
): SubscriptionEffect | null {
  switch (stored) {
    case 'COIN_DAILY_BONUS':
    case 'COIN_PERIOD_GRANT':
      return 'COIN_PERIOD_GRANT';
    case 'VIP_READING':
      return 'VIP_READING';
    default:
      return null;
  }
}

/**
 * 发币效果的写库存储值（C 端 `src/lib/subscription-effect.ts` 同名常量，两仓同值）：admin 写
 * `product_metadata.subscription_effect` 的发币效果一律经 `toStoredSubscriptionEffect` 取本常量，不写字面量。
 *
 * 取旧值 `COIN_DAILY_BONUS`，因为 C 端改名处于扩张期（srv `docs/plans/2026-10-13-subscription-period-grant.md`
 * §2.1）：部署窗口内仍在跑的旧 Worker、以及回滚后的旧 Worker 要求目录值 ∈ 旧闭集、成交快照与目录行逐字相等，
 * 目录行写 `COIN_PERIOD_GRANT` 会让它们判效果不一致、事件重试后进 DLQ。新代码读侧一律归一，旧值对它无差别。
 * 本常量与 C 端收缩 PR 切写入常量为 `COIN_PERIOD_GRANT` 同步切换，先于该侧回填与 CHECK 收紧。
 */
export const COIN_PERIOD_GRANT_STORED_WRITE_VALUE =
  'COIN_DAILY_BONUS' as const satisfies StoredSubscriptionEffect;

/** 对外效果 → 写库存储值：`COIN_PERIOD_GRANT` 取 `COIN_PERIOD_GRANT_STORED_WRITE_VALUE`，`VIP_READING` 原样。 */
export function toStoredSubscriptionEffect(effect: SubscriptionEffect): StoredSubscriptionEffect {
  return effect === 'COIN_PERIOD_GRANT' ? COIN_PERIOD_GRANT_STORED_WRITE_VALUE : effect;
}

/**
 * 建行时的对外订阅效果（A8 §2「SUBSCRIPTION 默认 `COIN_PERIOD_GRANT`」）：CONSUMABLE 恒 null；
 * SUBSCRIPTION 省略即 `COIN_PERIOD_GRANT`。落库前再经 `toStoredSubscriptionEffect` 换成存储值并**显式写入**，
 * 不交给 DB 触发器的 NULL 填充 —— 写入值只由该映射单点决定。
 */
export function resolveSubscriptionEffectDefault(
  productType: ProductType,
  effect: SubscriptionEffect | null | undefined,
): SubscriptionEffect | null {
  if (productType !== 'SUBSCRIPTION') return null;
  return effect ?? 'COIN_PERIOD_GRANT';
}

/**
 * `product_metadata.payment_method` 是**结算通道层**（`03-数据字典/70-产品配置.md` §3「两层闭集」）：
 * 一个结算通道一套 placement / provider 配置 / 产品与权益，与该通道下有几个面板可选项无关。
 * 结算通道闭集单点在 `./payment-methods`（`SETTLEMENT_CHANNELS`），本文件只取用、不另立；支付方式到结算通道的
 * 映射读 `system_payment_methods` 目录（70 §3.0），不在 admin 另维护。
 *
 * **不要在本域用支付方式层取值**：三个 `STRIPE_*` 各建一行 = 权益、价格路由、placement 各出现三份
 * 可分叉的副本，而 `(app_id, access_level_id, payment_method)` 唯一键与 Stripe 一整套约束都按通道建模。
 */
// 取用（不再导出）：闭集单点在 `./payment-methods`，两处导出会让 `PAYMENT_METHODS` 这个名字
// 在本域重新指回结算通道，正是 #675 那次混层的起点。

/**
 * `consumable_effect` 闭集（dev-docs `03-数据字典/70-产品配置.md` §2.5 单点定义）。
 * 仅 `product_type='CONSUMABLE'` 可非空；**未登记的效果值不构成可分派效果** —— C 端
 * `is_serveable` 不成立、履约命中即 fail-loud。DB CHECK `product_metadata_consumable_effect_valid`
 * 是 SQL 平行闭集（无编译期链，改动时两处人工同步）。
 */
export const CONSUMABLE_EFFECTS = ['COIN_GRANT', 'READING_PASS_CARD', 'NOVEL_PURCHASE'] as const;
export const ConsumableEffectSchema = z.enum(CONSUMABLE_EFFECTS);
export type ConsumableEffect = z.infer<typeof ConsumableEffectSchema>;

/**
 * 不发币效果闭集（§2.5 注册表「两列恒 `"0"`、`bonus_expire_days` 恒 `0`」那一类）：
 * 权益由效果自身承载，`coin_amount` / `bonus_amount` 与 `bonus_expire_days` 一律为 0。**按效果显式列举、不按「非 COIN_GRANT」取反** —— 闭集外的未登记值不属于本类，
 * 它既不可分派也不可售，取反会把它误纳入「合法的不发币产品」。
 */
export const ZERO_ENTITLEMENT_EFFECTS = ['READING_PASS_CARD', 'NOVEL_PURCHASE'] as const;

/** 该效果是否属于不发币类（coin / bonus + bonus_expire_days 恒 0）*/
export function isZeroEntitlementEffect(
  effect: ConsumableEffect | string | null,
): effect is 'READING_PASS_CARD' | 'NOVEL_PURCHASE' {
  return effect !== null && (ZERO_ENTITLEMENT_EFFECTS as readonly string[]).includes(effect);
}

/**
 * 存量阅读卡产品位字面量（`CARD_A` / `CARD_B` 仍合法）。**不再是取值闭集**：新卡 `tier_id` 为 UUID v7，
 * 取值域 = 非空无空白字符串（dev-docs#773 / 70 §2.5）。
 */
export const READING_PASS_TIER_IDS = ['CARD_A', 'CARD_B'] as const;
export const ReadingPassTierIdSchema = z.string().min(1).regex(/^\S+$/);
export type ReadingPassTierId = string;

/** 落库 / 响应侧的阅读卡 `tier_id`：非空且不含空白（新 UUID v7 与存量 CARD_A/CARD_B 都合法）*/
export function isReadingPassTierId(value: unknown): value is string {
  return typeof value === 'string' && /^\S+$/.test(value);
}

/**
 * `duration_seconds` 上界 = 库存列 `reading_pass_cards.duration_seconds` 的 Postgres int4 上限。
 * 超过它的时长在配置侧看着合法、一旦成交就在履约的 INSERT 上炸 —— 而那时钱已经收了。
 * 与 C 端可售判据、`product_metadata` 的 CHECK 取同一个上界，三处不分叉。
 */
export const MAX_READING_PASS_DURATION_SECONDS = 2147483647;

/** Postgres int8 上界，money 串兜底（防写 bigint 列溢出报 DB 错而非 422） */
const INT8_MAX = 9223372036854775807n;
/** Postgres int4 上界，bonus_expire_days 兜底 */
const INT4_MAX = 2147483647;
/** title_i18n 单值字符上限 + locale 数上限（防 jsonb / audit-log 膨胀） */
const TITLE_I18N_MAX_VALUE_LEN = 200;
const TITLE_I18N_MAX_LOCALES = 50;
/** 运营识别名 name 长度上界（A8 §2：1..100 非空白；DB 列 varchar(255) 更宽，取契约窄值） */
const NAME_MAX_LEN = 100;

/** int8 max 9223372036854775807 = 19 位；money 串位数上界（先于 BigInt 截断，防超大串烧 CPU） */
const MONEY_MAX_DIGITS = 19;
/** bigint money —— 非负整数串（minor units）；上界 int8 */
const MoneyStringSchema = z
  .string()
  .regex(/^(0|[1-9][0-9]*)$/, 'Must be non-negative integer string')
  // .max 先 cheap 截断超长串（百万位攻击串不进 BigInt）
  .max(MONEY_MAX_DIGITS, `too many digits for int8 (max ${MONEY_MAX_DIGITS})`)
  // refine 在 dirty 值上仍跑：① regex 失败值（'abc'）会令 BigInt 抛 → 先 test 守卫；
  // ② 超大位数串 → 先 length 守卫再 BigInt，避免在百万位串上 BigInt 烧 CPU/超时
  .refine(
    (v) => !/^(0|[1-9][0-9]*)$/.test(v) || (v.length <= MONEY_MAX_DIGITS && BigInt(v) <= INT8_MAX),
    { message: 'exceeds int8 max (9223372036854775807)' },
  );

/** 运营识别名（C 端 GET /payment/products 必返、客户端不显示；product 级、跨通道行应同值）；1..100 非空白 */
const NameSchema = z
  .string()
  .min(1)
  .max(NAME_MAX_LEN)
  .refine((v) => v.trim().length > 0, { message: 'name must not be blank' });

/**
 * access_level_id —— 客户端匹配键 + 跨渠道分组键（product 级、跨通道行应同值；#264/#385）。Adapty Dashboard 手工维护、
 * **可空**：NULL = 待运营从 Dashboard 补，NULL 行不进 C 端 serving（无匹配键、无法归组，作发布 gate）。
 * 非空须 1..255 且**无空白**（`^\S+$`）：它是 C 端 exact-match 键，'' / 纯空白 / 带首尾空白会被 C 端 serving / DB
 * partial unique 视作「已配」却永不命中 = 死配置；与同为 exact-match 键的 vendor_product_id 同口径拒空白。
 * 运营手输（getPaywall 不返本字段）。
 */
const AccessLevelIdSchema = z
  .string()
  .min(1)
  .max(255)
  .regex(/^\S+$/, 'access_level_id must not contain whitespace')
  .nullable();

/**
 * 多语言标题 jsonb；商品展示及仍读取 live 名称池的历史记录按 C 端既有解析规则消费。
 * key = BCP 47 受限子集（与 LanguageCodeSchema / allowed_languages CHECK 同口径）；
 * value ≤200 字符且非纯空白、1..50 locale —— 防非 locale key 漏 C 端 fallback + jsonb 膨胀。
 * **必含非空白 `en`**：en 是系统确定性 fallback 语言（apps.allowed_languages 必含 en / system_languages
 * en 不可禁用）；订单历史 / 推送渲染 product 名按 X-Language 取不到时 fallback en，缺 en 会渲出空 productName。
 * key 正则 / en 必填 / 空白 value 均在 superRefine 显式校验。
 */
export const TitleI18nSchema = z
  .record(z.string(), z.string().min(1).max(TITLE_I18N_MAX_VALUE_LEN))
  .superRefine((val, ctx) => {
    const keys = Object.keys(val);
    if (keys.length < 1 || keys.length > TITLE_I18N_MAX_LOCALES) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `title_i18n requires 1..${TITLE_I18N_MAX_LOCALES} locales`,
      });
    }
    for (const [k, v] of Object.entries(val)) {
      if (!LANGUAGE_CODE_RE.test(k)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `invalid locale key (need BCP 47 language or language-REGION): ${k}`,
        });
      }
      if (v.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `title_i18n[${k}] must not be blank`,
        });
      }
    }
    // en 确定性 fallback 必须存在且非空白
    if (typeof val.en !== 'string' || val.en.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'title_i18n must include a non-blank "en" (deterministic fallback)',
      });
    }
  });
export type TitleI18n = z.infer<typeof TitleI18nSchema>;

/**
 * stripe_price_id —— STRIPE direct Checkout 的 Price 路由标识（A8 §9.6 / 03-数据字典/70 §2，#352）。
 * canonical 格式 `^price_[A-Za-z0-9]{1,249}$`；**仅 STRIPE 行可非空，IAP 行必须为 null**。
 * 不承载标题、金额、币种、周期、offer 或可售状态——**不参与 `is_serveable`**，漏配不影响 Adapty 商品
 * discovery 与付款后 webhook/S3 履约，只让 direct Checkout 发起侧 fail-closed。
 */
const StripePriceIdSchema = z
  .string()
  .regex(/^price_[A-Za-z0-9]{1,249}$/, 'stripe_price_id must match ^price_[A-Za-z0-9]{1,249}$')
  .nullable();

/**
 * stripe_price_id 与通道的一致性（A8 §2 校验② / §3 / §9.6）：只有 STRIPE 行可非 null，IAP 与框架通道行恒 null
 * （框架通道原生价格只经 §10.3 `payment_channel_prices` 维护，不写本列）。
 * 纯函数 → Create superRefine（用请求 payment_method）与 update-service（用已存 payment_method）共用，
 * 因 Update 契约不携带 payment_method，无法在 Zod 层独立校验（同 assertProductTypeConsistency 模式）。
 * 返回错误信息（不一致）或 null（一致）。
 */
export function assertStripePriceIdChannel(
  paymentMethod: SettlementChannel,
  stripePriceId: string | null | undefined,
): string | null {
  if (paymentMethod !== 'STRIPE' && stripePriceId != null) {
    return `${paymentMethod} product must keep stripe_price_id null`;
  }
  return null;
}

/**
 * 框架通道行只售 CONSUMABLE（A8 §2 校验③ / 70 §2：能力声明 `product_types = CONSUMABLE`）。纯函数，供 Create
 * superRefine 判请求；落库行的兜底在可售判定（框架通道订阅行 → `PRODUCT_FIELDS_INVALID`，写门禁据此拒）。返回错误信息或 null。
 */
export function assertFrameworkChannelProductType(
  paymentMethod: SettlementChannel,
  productType: ProductType,
): string | null {
  if (isFrameworkSettlementChannel(paymentMethod) && productType !== 'CONSUMABLE') {
    return `${paymentMethod} product only supports product_type CONSUMABLE`;
  }
  return null;
}

/**
 * `consumable_effect_params` 的**容器**校验（jsonb object；逐效果 shape 交
 * `assertConsumableEffectShape`）。数组 / 标量在此即拒 —— DB 列是 jsonb，写进标量不报错却让
 * 所有 `->>` 取值返 NULL，效果参数静默丢失。
 */
export const ConsumableEffectParamsSchema = z.record(z.string(), z.unknown());
export type ConsumableEffectParams = z.infer<typeof ConsumableEffectParamsSchema>;

/** 逐效果 shape 校验的金额入参（**有效值**：patch 后 / prefill 后；undefined = 本次不涉及、跳过）*/
export type ConsumableEffectAmounts = {
  coin_amount?: string | null;
  bonus_amount?: string | null;
  bonus_expire_days?: number | null;
};

function isNonZeroMoney(value: string | null | undefined): boolean {
  return value !== undefined && value !== null && value !== '0';
}

/**
 * `consumable_effect_params` 逐效果 shape + 权益列取值约束（dev-docs `03-数据字典/70-产品配置.md` §2.5
 * 「consumable_effect_params 逐效果 shape」表 + `02-API契约/A8-运营支付.md` §2 / §3）。
 *
 * 纯函数 → create 的 Zod superRefine（用请求 product_type / effect）与 update-service（用**已存**
 * effect，Update 契约不携带 `consumable_effect`）共用，同 `assertProductTypeConsistency` 模式。
 *
 * - `effect === null`（含 SUBSCRIPTION 行）：params 恒 `{}`
 * - `COIN_GRANT`：params 恒 `{}`，任何 key 均非法（发币量走 coin/bonus 列，不进 params）
 * - `READING_PASS_CARD`：按 `mode` 分两套 shape（A8 §2 / #773）——
 *   - `'request'`（创建 / 复活请求）：**只许 `duration_seconds`**，出现 `tier_id` → 拒（IAP 由服务端
 *     `newId()`、STRIPE 从 IAP 存活基线继承、复活沿用历史 `tier_id`）
 *   - `'stored'`（落库 / 响应 / PATCH 合并后）：恰 `tier_id`（非空无空白）+ `duration_seconds`
 *   且 `coin_amount` / `bonus_amount` / `bonus_expire_days` 恒 0。
 * - `NOVEL_PURCHASE`：params 恒 `{}`，任何 key 均非法（**产品不承载价格档**：同一买断产品被多个价格档、
 *   多本书复用，买断哪一本由购买链路的 `novel_id` 决定），coin / bonus 与 `bonus_expire_days` 同样恒 0。
 *
 * `amounts` 只校验**显式给出**的项（undefined 跳过）：STRIPE 创建时金额由服务端按 IAP 基线 prefill，
 * 校验须落在 prefill 之后的有效值上，调用方按其可见性分两次传入。
 *
 * @returns `null` = 合规；否则 `{ field, message }`（field 用于 `VALIDATION_FAILED` 的 errors[] 定位）
 */
export type ConsumableEffectShapeMode = 'request' | 'stored';

export function assertConsumableEffectShape(
  effect: ConsumableEffect | null,
  params: Record<string, unknown> | null | undefined,
  amounts: ConsumableEffectAmounts = {},
  mode: ConsumableEffectShapeMode = 'stored',
): { field: string; message: string } | null {
  const keys = params === null || params === undefined ? [] : Object.keys(params);

  if (effect === null || effect === 'COIN_GRANT' || effect === 'NOVEL_PURCHASE') {
    if (keys.length !== 0) {
      return {
        field: 'consumable_effect_params',
        message:
          effect === null
            ? 'consumable_effect 为空时 consumable_effect_params 必须是空对象 {}'
            : effect === 'COIN_GRANT'
              ? 'COIN_GRANT 的 consumable_effect_params 必须是空对象 {}（发币量走 coin/bonus 列）'
              : 'NOVEL_PURCHASE 的 consumable_effect_params 必须是空对象 {}（产品不承载价格档，买断哪一本由购买链路的 novel_id 决定）',
      };
    }
    // 空 params 之外，NOVEL_PURCHASE 还要求 coin / bonus 与有效期恒 0（§2.5 effect_wellformed）
    return effect === 'NOVEL_PURCHASE' ? findNonZeroEntitlement('NOVEL_PURCHASE', amounts) : null;
  }

  // READING_PASS_CARD：创建请求只许 duration_seconds；落库 / 响应恰两 key（#773）
  const durationSeconds = params?.duration_seconds;
  if (mode === 'request') {
    if (keys.includes('tier_id')) {
      return {
        field: 'consumable_effect_params.tier_id',
        message: '创建 / 复活阅读卡禁止传入 tier_id（IAP 由服务端生成，STRIPE 从 IAP 基线继承）',
      };
    }
    if (keys.length !== 1 || !keys.includes('duration_seconds')) {
      return {
        field: 'consumable_effect_params',
        message: 'READING_PASS_CARD 的请求 params 只许 duration_seconds（禁止传 tier_id）',
      };
    }
  } else {
    const tierId = params?.tier_id;
    if (keys.length !== 2 || !keys.includes('tier_id') || !keys.includes('duration_seconds')) {
      return {
        field: 'consumable_effect_params',
        message:
          'READING_PASS_CARD 的 consumable_effect_params 必须恰含 tier_id 与 duration_seconds 两个 key',
      };
    }
    if (!isReadingPassTierId(tierId)) {
      return {
        field: 'consumable_effect_params.tier_id',
        message: 'tier_id 必须是非空且不含空白的字符串（新卡 UUID v7，存量 CARD_A / CARD_B 合法）',
      };
    }
  }
  // 判 isSafeInteger 而非 isInteger：params 在 jsonb 里，超过 2^53-1 的整数经 JSON 解析会被静默
  // 舍入成另一个「合法整数」—— 存进去的与下发的不是同一个数，故 fail-closed 拒收。
  if (
    typeof durationSeconds !== 'number' ||
    !Number.isSafeInteger(durationSeconds) ||
    durationSeconds <= 0 ||
    durationSeconds > MAX_READING_PASS_DURATION_SECONDS
  ) {
    return {
      field: 'consumable_effect_params.duration_seconds',
      message: `duration_seconds 必须是 (0, ${MAX_READING_PASS_DURATION_SECONDS}] 内的整数`,
    };
  }
  return findNonZeroEntitlement('READING_PASS_CARD', amounts);
}

/**
 * 不发币效果的权益列恒零校验（§2.5：`coin_amount` / `bonus_amount` / `bonus_expire_days` 恒 0；
 * 冻结列 `daily_grant_amount` 不参与声明）。两个不发币效果共用一份判据，避免逐效果各写一遍而漂移。
 * 只校验**显式给出**的项（undefined 跳过），与 `assertConsumableEffectShape` 的 `amounts` 语义一致。
 */
function findNonZeroEntitlement(
  effect: (typeof ZERO_ENTITLEMENT_EFFECTS)[number],
  amounts: ConsumableEffectAmounts,
): { field: string; message: string } | null {
  for (const [field, value] of [
    ['coin_amount', amounts.coin_amount],
    ['bonus_amount', amounts.bonus_amount],
  ] as const) {
    if (isNonZeroMoney(value)) {
      return { field, message: `${effect} 不发币，${field} 必须为 "0"` };
    }
  }
  if (amounts.bonus_expire_days !== undefined && (amounts.bonus_expire_days ?? 0) !== 0) {
    return {
      field: 'bonus_expire_days',
      message: `${effect} 的 bonus_expire_days 必须为 0`,
    };
  }
  return null;
}

/**
 * 效果与 `product_type` 的绑定（A8 §2）：`SUBSCRIPTION` 传非 null `consumable_effect` 即
 * `VALIDATION_FAILED`。**CONSUMABLE 缺省不在此拒**——服务端按 migration 0113 回填口径补 `COIN_GRANT`
 * （契约 A8 §2 该字段「必填=否、默认 null」，硬拒会把既有录入流程打断）。
 */
export function assertConsumableEffectProductType(
  productType: ProductType,
  effect: ConsumableEffect | null | undefined,
): { field: string; message: string } | null {
  if (productType === 'SUBSCRIPTION' && effect != null) {
    return {
      field: 'consumable_effect',
      message: 'SUBSCRIPTION product must not set consumable_effect',
    };
  }
  return null;
}

/**
 * 建 CONSUMABLE 行时的效果缺省（server migration 0113 存量回填口径：既有 CONSUMABLE 行一律 `COIN_GRANT`）。
 * SUBSCRIPTION 行恒 null。缺省必须与回填口径一致，否则新建的发币商品 `consumable_effect=NULL` →
 * C 端 `is_serveable` 不成立 → 商品静默不可售。
 */
export function resolveConsumableEffectDefault(
  productType: ProductType,
  effect: ConsumableEffect | null | undefined,
): ConsumableEffect | null {
  if (productType !== 'CONSUMABLE') return null;
  return effect ?? 'COIN_GRANT';
}

/**
 * product_type 跨字段一致性（dev-docs A8-运营支付 §2 校验③ + 03-数据字典/70 §2）：
 * - CONSUMABLE：不得带 subscription_period，`subscription_effect` 恒 null
 * - SUBSCRIPTION：`VIP_READING` 的 bonus_amount 必须 "0"（该效果不发币）；`COIN_PERIOD_GRANT` 的 bonus
 *   与 CONSUMABLE 同一规则（IAP 人工维护、STRIPE ≥ 派生下限，下限由 serveability validator 判）
 * 纯函数 → create superRefine（用请求值）与 update / activate（用已存 product_type / subscription_effect）共用，
 * 因 Update 契约不携带 product_type / subscription_effect，无法在 Zod 层独立校验。返回错误信息或 null。
 * `subscription_effect` 收**存储值**（含旧值 `COIN_DAILY_BONUS`），比较前归一；bonus_amount 是整数串，
 * "0" 为唯一零形，字符串等值即可。
 */
export function assertProductTypeConsistency(
  productType: ProductType,
  fields: {
    subscription_period?: SubscriptionPeriod | null;
    subscription_effect?: StoredSubscriptionEffect | SubscriptionEffect | string | null;
    bonus_amount?: string | null;
  },
): string | null {
  if (productType === 'CONSUMABLE') {
    if (fields.subscription_period != null) {
      return 'CONSUMABLE product must not set subscription_period';
    }
    if (fields.subscription_effect != null) {
      return 'CONSUMABLE product must not set subscription_effect';
    }
  }
  if (productType === 'SUBSCRIPTION') {
    if (
      normalizeSubscriptionEffect(fields.subscription_effect) === 'VIP_READING' &&
      fields.bonus_amount != null &&
      fields.bonus_amount !== '0'
    ) {
      return 'VIP_READING subscription must keep bonus_amount at "0"';
    }
  }
  return null;
}

/**
 * 创建 product_metadata（运营在 Adapty 加 product 后，按 (vendor_product_id, payment_method) 在 admin 录业务效果）。
 * per-通道：每个 product 一条 IAP 基线行，另按需录 STRIPE 与已登记框架通道行（各自权益可不同）。IAP 的 coin/bonus
 * 省略即 "0"；CONSUMABLE 不得带 subscription_period / subscription_effect；`VIP_READING` 订阅 bonus_amount 必须 "0"
 * ——均 superRefine 强制。冻结列 `daily_grant_amount` 不在契约内，误传按未声明键 VALIDATION_FAILED（A8 §2 ①）。
 * **bonus_ratio 不在录入契约（#465）**：STRIPE 与框架通道权益由服务端按 apps.third_party_bonus_ratio 派生受控，
 * bonus_ratio 改响应派生字段——.strict() 使误传即 VALIDATION_FAILED。
 * **access_level_id：各结算通道行统一必填非空**（A8 §2，fresh insert 与复活同此）：缺失即 VALIDATION_FAILED；
 * 服务端进一步校「须命中当前 App 的 active 产品级关联（§8.3）」与 STRIPE / 框架通道「唯一命中 IAP 存活基线」。
 * **框架通道行**（A8 §2「框架通道行」）：只收 CONSUMABLE；`vendor_product_id` 是该通道在 Adapty 自定义 store 的
 * store product ID（不要求 `prod_*`）；`stripe_price_id` 恒 null；原生成交价不经本端点 —— 金额 / 币种字段不在
 * 本契约，误传按 .strict() 拒（校验 ①），价格只走 §10.3。
 */
export const CreateProductMetadataRequestSchema = z
  .object({
    app_id: z.string().uuid(),
    /**
     * Adapty Dashboard 上的 product vendor_product_id（不可后续改，改 ID = 删旧 + 加新）。
     * 拒任何空白字符：它是 C 端 exact-match 反查键，含空白会永不命中 → 漏加积分。Adapty 平台 product id 本不含空格。
     */
    vendor_product_id: z
      .string()
      .min(1)
      .max(255)
      .regex(/^\S+$/, 'vendor_product_id must not contain whitespace'),
    /** 结算通道（PK 组件）：取值域 = §3 两层闭集的 `SETTLEMENT_CHANNELS` 层；创建后不可改（改通道 = 删旧 + 加新）*/
    payment_method: SettlementChannelSchema,
    /**
     * STRIPE direct Checkout 的 Price 路由（A8 §9.6，#352）：STRIPE 行可选、IAP 与框架通道行必须为 null（superRefine 强制）。
     * 省略 = null；同 App active STRIPE 行内唯一（service 层查冲突 → RESOURCE_CONFLICT）。
     */
    stripe_price_id: StripePriceIdSchema.optional(),
    product_type: ProductTypeSchema,
    /**
     * 订阅效果（A8 §2）：仅 SUBSCRIPTION 可非空，CONSUMABLE 传非 null 即 VALIDATION_FAILED。
     * SUBSCRIPTION 省略 = `COIN_PERIOD_GRANT`（服务端 resolveSubscriptionEffectDefault + toStoredSubscriptionEffect 显式落库）。
     * 只收对外闭集：旧存储值 `COIN_DAILY_BONUS` 不是可写值，传入即 422。创建后不可改（不在 Update 契约）。
     */
    subscription_effect: SubscriptionEffectSchema.nullable().optional(),
    /**
     * 仅 SUBSCRIPTION 有意义；CONSUMABLE 必须留空（superRefine 强制）。
     * .nullable() 与 Update / Response / DB（NULL）一致：前端空 select 序列化为 null 不应 422；
     * assertProductTypeConsistency 用 `!= null` 同等对待 null/undefined。
     */
    subscription_period: SubscriptionPeriodSchema.nullable().optional(),
    /**
     * 客户端匹配 + 跨渠道分组键（product 级）：各结算通道行必填非空（superRefine 强制，A8 §2）。关联在 §8.3 建立后
     * 固定、PATCH 不收，故不再允许「先留空待补」—— 留空的行只能删除后带 access_level_id 重录（走复活）。
     * 类型保留 nullable / optional 只为让「缺失」落到带 field 的 VALIDATION_FAILED，而非类型层的笼统报错。
     */
    access_level_id: AccessLevelIdSchema.optional(),
    /** 运营识别名（product 级，跨通道行应同值）；1..100 非空白 */
    name: NameSchema,
    title_i18n: TitleI18nSchema,
    // coin/bonus 通道特定默认（#465），改 service 层填：IAP 省略→"0"；STRIPE 省略→按 IAP 基线 + App 比例
    // 派生（coin 复制基线 / bonus 预填派生下限，CONSUMABLE 发币与 COIN_PERIOD_GRANT 订阅同一公式）。
    // 故 Zod 层 optional 不 .default——保留「省略」信号供 service 区分。
    coin_amount: MoneyStringSchema.optional(),
    bonus_amount: MoneyStringSchema.optional(),
    /** bonus 有效期天数；0 = 永久不过期；上界 int4 防溢出 */
    bonus_expire_days: z.number().int().nonnegative().max(INT4_MAX).default(0),
    /**
     * 一次性产品的权益效果（A8 §2）：仅 CONSUMABLE 可非空，SUBSCRIPTION 传非 null 即 VALIDATION_FAILED。
     * **省略 = 按 migration 0113 回填口径写 `COIN_GRANT`**（服务端 resolveConsumableEffectDefault），
     * 不是写 NULL —— NULL 的 CONSUMABLE 行在 C 端不可售（`MISSING_CONSUMABLE_EFFECT`）。
     */
    consumable_effect: ConsumableEffectSchema.nullable().optional(),
    /** 该效果的参数（A8 §2）；逐效果 shape 见 §2.5，superRefine 按**有效** effect 校验。省略 = `{}` */
    consumable_effect_params: ConsumableEffectParamsSchema.optional(),
    // is_active / bonus_ratio / daily_grant_amount 不在 Create/Update 契约（安全收窄 + 派生 + 冻结列）：创建 / 复活
    // 一律草稿 isActive=false（#773），启停只经 POST /:id/activate|deactivate 与 replace；bonus_ratio 响应派生（#465）；
    // daily_grant_amount 冻结、admin 不写（70 §2）。
  })
  // .strict()：拒未知键（与 Update 一致）—— 拼错字段（如驼峰 coinAmount）会被 strip 后以默认 "0" 建行、
  // 静默发错积分；strict 让 typo 直接 422 而非静默默认；误传 bonus_ratio / is_active / daily_grant_amount 亦 422
  .strict()
  .superRefine((v, ctx) => {
    const err = assertProductTypeConsistency(v.product_type, {
      subscription_period: v.subscription_period,
      subscription_effect: v.subscription_effect,
      bonus_amount: v.bonus_amount,
    });
    if (err) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [
          /subscription_effect/.test(err)
            ? 'subscription_effect'
            : /bonus_amount/.test(err)
              ? 'bonus_amount'
              : 'subscription_period',
        ],
        message: err,
      });
    }
    // 各结算通道行必须携带 access_level_id（A8 §2）：STRIPE 与框架通道据此唯一命中 IAP 基线派生权益，
    // IAP 行是逻辑产品的基线、缺它整个产品无从归组进 serving
    if (v.access_level_id === null || v.access_level_id === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['access_level_id'],
        message: `${v.payment_method} product requires access_level_id (product-level link, fixed once created)`,
      });
    }
    // 框架通道只售 CONSUMABLE（A8 §2 校验③）
    const frameworkTypeErr = assertFrameworkChannelProductType(v.payment_method, v.product_type);
    if (frameworkTypeErr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['product_type'],
        message: frameworkTypeErr,
      });
    }
    // IAP 与框架通道行禁写 stripe_price_id（A8 §2 校验② / §9.6）
    const priceErr = assertStripePriceIdChannel(v.payment_method, v.stripe_price_id);
    if (priceErr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['stripe_price_id'],
        message: priceErr,
      });
    }
    // SUBSCRIPTION 不得带效果（A8 §2）
    const bindErr = assertConsumableEffectProductType(v.product_type, v.consumable_effect);
    if (bindErr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [bindErr.field],
        message: bindErr.message,
      });
      return;
    }
    // 逐效果 params shape：effect 取**服务端将落库的有效值**（CONSUMABLE 省略 → COIN_GRANT），
    // 否则「省略 effect + 传 READING_PASS_CARD 的 params」会在 Zod 放行、落库时才被 DB CHECK 拦成 500。
    // 金额只校验请求显式给出的项——STRIPE 省略时由服务端按 IAP 基线 prefill，其有效值在 service 层复校。
    const effectErr = assertConsumableEffectShape(
      resolveConsumableEffectDefault(v.product_type, v.consumable_effect),
      v.consumable_effect_params,
      {
        coin_amount: v.coin_amount,
        bonus_amount: v.bonus_amount,
        bonus_expire_days: v.bonus_expire_days,
      },
      'request',
    );
    if (effectErr) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: effectErr.field.split('.'),
        message: effectErr.message,
      });
    }
  });
export type CreateProductMetadataRequest = z.infer<typeof CreateProductMetadataRequestSchema>;

/**
 * 更新 product_metadata 业务效果（运营改加多少 coin/bonus/name 等；bonus_ratio 改响应派生不可写，#465）。
 * **.strict()**：拒绝一切未声明键 —— vendor_product_id / payment_method / product_type / app_id / id / is_active /
 * 时间戳等 Adapty 身份与不可变/收窄字段若误传一律 422，而非静默 strip（fail-open 会让运营误以为改了 ID/通道）。
 * **is_active 不在本契约（安全收窄）**：启停经独立 POST /:id/activate|deactivate 收窄 root/admin——
 * operator 经 PATCH is_active=false 与 DELETE 软删对 C 端同效，故 is_active 误传一律 422。
 * **subscription_effect 不在本契约**（创建后不可换，70 §2）；冻结列 **daily_grant_amount 不在本契约**（A8 §3「不可改」）。
 * product_type 跨字段一致性（period 仅订阅 / VIP_READING bonus 恒 0）须由 service 以已存 product_type /
 * subscription_effect 调 assertProductTypeConsistency 校验（Update 体不携带二者，Zod 层无法独立判）。
 * **access_level_id 不在本契约**（A8 §3，IAP / STRIPE / 框架通道一视同仁）：它是产品级关联（§8.3 建立并固定），
 * 改挂或清空 IAP 行会让配对 STRIPE 与框架通道行失去基线而退出可售；需要另一个 access level 时创建新产品。
 */
export const UpdateProductMetadataRequestSchema = z
  .object({
    /** product 级识别名（可改；改后运营须同步改同 product 另一通道行以保持同值）*/
    name: NameSchema.optional(),
    // access_level_id 不在本契约（A8 §3）：产品级关联、渠道行只继承，`.strict()` 拒传；
    // 服务端入口先于本 schema 判定，以带上 details.reason（见 PRODUCT_ACCESS_LEVEL_CHANNEL_PATCH_FORBIDDEN）
    /**
     * STRIPE direct Checkout Price 路由（A8 §3/§9.6，#352）：可改 / 可传 null 清空。IAP 行写非 null 由
     * service 以已存 payment_method 调 assertStripePriceIdChannel 拒（Update 体不携带 payment_method）。
     * 改动只影响新 operation——既有 operation 继续用固化快照。
     */
    stripe_price_id: StripePriceIdSchema.optional(),
    subscription_period: SubscriptionPeriodSchema.nullable().optional(),
    title_i18n: TitleI18nSchema.optional(),
    coin_amount: MoneyStringSchema.optional(),
    bonus_amount: MoneyStringSchema.optional(),
    // bonus_ratio 不可写（#465 响应派生）、daily_grant_amount 冻结（70 §2）：误传即 VALIDATION_FAILED（.strict）
    bonus_expire_days: z.number().int().nonnegative().max(INT4_MAX).optional(),
    /**
     * 效果参数（A8 §3）。**`consumable_effect` 本身不可改**（不在本契约、`.strict()` 拒传，与 product_type 同档）；
     * params 内 **`tier_id` 不可改**（产品位身份是客户端与存量 `reading_pass_cards` 的关联键）。
     * 收到本字段 = **逻辑产品级原子更新**：service 解析 `:id` 为 `(app_id, access_level_id)`、
     * 同一事务锁 IAP / STRIPE 配对行同写（见 product-metadata-admin-service）。
     */
    consumable_effect_params: ConsumableEffectParamsSchema.optional(),
  })
  .strict()
  .refine((b) => Object.keys(b).length >= 1, { message: 'at least one field required' });
export type UpdateProductMetadataRequest = z.infer<typeof UpdateProductMetadataRequestSchema>;

/**
 * A8 §3：PATCH 传入 `access_level_id`（含 null）→ `VALIDATION_FAILED`，`details.reason` 取本值。
 * 服务端在 Update schema 解析前按「请求体是否带该键」判定，与其余未声明键的 `.strict()` 拒绝区分开。
 */
export const PRODUCT_ACCESS_LEVEL_CHANNEL_PATCH_FORBIDDEN =
  'PRODUCT_ACCESS_LEVEL_CHANNEL_PATCH_FORBIDDEN';

/**
 * product_metadata 响应 DTO（DB 列名 → API 字段；bigint → string；不含软删行；冻结列 daily_grant_amount 不出响应）。
 * **派生字段（非 DB 列，#465）**：bonus_ratio / is_serveable / derived_minimum_* / validation_issues 由服务端
 * 按同 App 候选集 + apps.third_party_bonus_ratio 跑 evaluateProductServeability 实时派生（与 C 端 serving 逐例等价）。
 */
export type ProductMetadataResponse = {
  id: string;
  app_id: string;
  vendor_product_id: string;
  /** 客户端匹配 + 跨渠道分组键（product 级）；null = 待运营从 Dashboard 补（NULL 不进 C 端 serving）*/
  access_level_id: string | null;
  /** 结算通道（PK 组件，per-通道）：取值域 = §3 两层闭集的 `SETTLEMENT_CHANNELS` 层 */
  payment_method: SettlementChannel;
  /** STRIPE direct Checkout Price 路由；IAP 与框架通道行恒 null。不下发 C 端、不参与 is_serveable（A8 §9.6）*/
  stripe_price_id: string | null;
  product_type: ProductType;
  /**
   * 订阅效果（对外闭集）；CONSUMABLE 恒 null。存量存储值 `COIN_DAILY_BONUS` 经 normalizeSubscriptionEffect
   * 归一为 `COIN_PERIOD_GRANT` 后输出 —— 响应不出现旧值。
   */
  subscription_effect: SubscriptionEffect | null;
  /** null 表示非订阅 / 未配置 */
  subscription_period: SubscriptionPeriod | null;
  /** 运营识别名（product 级）*/
  name: string;
  title_i18n: TitleI18n;
  coin_amount: string;
  bonus_amount: string;
  /** 该通道单产品赠送比例（**响应派生**，非 DB 列）：CONSUMABLE 与 SUBSCRIPTION 同口径 bonus÷coin ROUND_HALF_UP 去尾零；零值 "0" */
  bonus_ratio: string;
  bonus_expire_days: number;
  /**
   * 一次性产品的权益效果（`03-数据字典/70-产品配置.md` §2 列，闭集见 §2.5）；SUBSCRIPTION 行恒 null。
   * CONSUMABLE 行为 null = 该行在 C 端不可售（履约无从分派）。
   */
  consumable_effect: ConsumableEffect | null;
  /** 该效果的参数（§2.5 逐效果 shape）；`COIN_GRANT` / 无效果时为 `{}` */
  consumable_effect_params: ConsumableEffectParams;
  is_active: boolean;
  /**
   * 首次发布时刻（ISO）。首次 activate / replace 上架写入，此后不改写；deactivate 不清除。
   * null = 从未发布的草稿。配对任一行非空即锁 duration_seconds。
   */
  first_published_at: string | null;
  /** 当前是否可进入 C 端新购链路（读时派生，异于运营开关 is_active）*/
  is_serveable: boolean;
  /**
   * STRIPE CONSUMABLE（发币）与 STRIPE `COIN_PERIOD_GRANT` SUBSCRIPTION 当前派生 bonus 下限（bigint string，
   * 同一公式 `IAP bonus + ceil(IAP coin × ratio)`）；其他行 / 无基线 null
   */
  derived_minimum_bonus_amount: string | null;
  /** 不可售原因闭集（可售时 []）*/
  validation_issues: ProductValidationIssue[];
  /**
   * 框架通道行的只读价格汇总（A8 §1，与 §10.3 GET `prices[]` 同源，按币种升序）；无价格行为 `[]`；
   * IAP 与 STRIPE 行恒 null。写价格只经 §10.3
   */
  channel_prices_summary: ChannelPriceSummaryItem[] | null;
  created_at: string;
  updated_at: string;
};

/**
 * 原子换代（A8 §5.1）：按 **access_level_id 字符串**寻址，不要用 UUID schema 把存量 slug 拒掉。
 * `activate` 非空；`deactivate` 可空；同一 id 不得同时出现在两边。
 */
const ReadingPassAccessLevelAddressSchema = z
  .string()
  .min(1)
  .regex(/^\S+$/, 'access_level_id must not contain whitespace');

export const ReplaceReadingPassTiersRequestSchema = z
  .object({
    app_id: z.string().uuid(),
    activate: z.array(ReadingPassAccessLevelAddressSchema).min(1),
    deactivate: z.array(ReadingPassAccessLevelAddressSchema).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    const deactivate = v.deactivate ?? [];
    if (v.activate.some((id) => deactivate.includes(id))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['activate'],
        message: '同一 access_level_id 不能同时出现在 activate 与 deactivate',
      });
    }
  });
export type ReplaceReadingPassTiersRequest = z.infer<typeof ReplaceReadingPassTiersRequestSchema>;
